import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { X, ArrowLeft, Menu, Wallet, Search, ChevronDown, Printer, CreditCard, Smartphone, HelpCircle, Banknote, BookUser, FileText, Receipt, Utensils, Package, Truck, RefreshCw, Loader2, MessageCircle, Image as ImageIcon } from 'lucide-react';
import LogoLoader from '../components/LogoLoader';
import PrinterSettings from '../components/PrinterSettings';
import nativeThermalPrinter from '../services/nativeThermalPrinterService';
import FOSWatermark from '../components/FOSWatermark';
import GuidedTour from '../components/GuidedTour';
import VoiceOrderButton from '../components/VoiceOrderButton';
import CustomerTagSection from '../components/CustomerTagSection';
import { TOUR_DEFINITIONS } from '../services/tourService';
import { MenuItemsSkeleton, CategoriesSkeleton, PaymentModalSkeleton } from '../components/SkeletonLoaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment method options
const PAYMENT_METHODS = [
  { id: 'cash', name: 'Cash', icon: Banknote, color: 'green', description: 'Direct cash payment' },
  { id: 'digital', name: 'Digital', icon: Smartphone, color: 'blue', description: 'UPI, PayTM, GPay, etc.' },
  { id: 'card', name: 'Card', icon: CreditCard, color: 'purple', description: 'Debit/Credit Card' },
  { id: 'other', name: 'Other', icon: HelpCircle, color: 'gray', description: 'Custom payment method' },
];

const GenerateOrder = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(true); // For skeleton loaders
  const [showCategories, setShowCategories] = useState(false);
  const [parties, setParties] = useState([]);
  const [partiesLoading, setPartiesLoading] = useState(false);
  
  // Tour state - check if navigated with tour param
  const [forceTour, setForceTour] = useState(searchParams.get('tour') === 'true');
  
  // Unified payment state
  const [selectedLedger, setSelectedLedger] = useState('cash');
  const [selectedLedgerName, setSelectedLedgerName] = useState('Cash');
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentMethodOther, setPaymentMethodOther] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [partySearchQuery, setPartySearchQuery] = useState('');
  
  // Printer related states
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  
  // Menu image display toggle (default ON)
  const [showMenuImages, setShowMenuImages] = useState(true);
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerSettings, setPrinterSettings] = useState(null);
  const saveDropdownRef = useRef(null);
  
  // E-Bill (WhatsApp) modal state
  const [showEBillModal, setShowEBillModal] = useState(false);
  const [eBillData, setEBillData] = useState({ customerName: '', phoneNumber: '' });
  const [sendingEBill, setSendingEBill] = useState(false);
  
  // Advanced Billing states
  const [isAdvancedBilling, setIsAdvancedBilling] = useState(false);
  const [orderType, setOrderType] = useState('dine_in');
  const [tableNo, setTableNo] = useState('');
  const [tokenNo, setTokenNo] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [customerGstin, setCustomerGstin] = useState('');
  const [isIgst, setIsIgst] = useState(false);
  const [franchiseGstStatus, setFranchiseGstStatus] = useState(null);
  const [showAdvancedPanel, setShowAdvancedPanel] = useState(false);
  
  // FOS Connect customer state
  const [customerData, setCustomerData] = useState({
    customer_id: null,
    customer_name_snapshot: null,
    customer_mobile_snapshot: null,
    customer_tag_status: 'guest',
    customer_name: null,
    customer_mobile: null,
  });
  const customerTagRef = useRef(null);

  // When a customer is tagged, clear any party ledger selection
  useEffect(() => {
    if (customerData.customer_id && selectedPartyId) {
      setSelectedPartyId(null);
      setSelectedLedger('cash');
      setSelectedLedgerName('Cash');
      setPaymentMethod('cash');
    }
  }, [customerData.customer_id]);
  
  // Order type options
  const ORDER_TYPES = [
    { id: 'dine_in', name: 'Dine In', icon: Utensils, description: 'Customer dining at restaurant' },
    { id: 'takeaway', name: 'Takeaway', icon: Package, description: 'Customer picks up order' },
    { id: 'delivery', name: 'Delivery', icon: Truck, description: 'Order delivered to customer' },
  ];

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load data on mount
  useEffect(() => {
    const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id || user?.franchise_id;
    
    if (!franchiseId) {
      console.warn('[GenerateOrder] No franchise ID - skipping data load');
      setInitialLoading(false);
      return;
    }
    
    let isMounted = true;
    const abortController = new AbortController();
    
    const loadData = async () => {
      setInitialLoading(true);
      
      try {
        // Load menu items and parties in parallel
        await Promise.all([
          fetchMenuItems(abortController.signal),
          fetchParties(false, abortController.signal)
        ]);
        
        // Load printer settings from localStorage
        fetchPrinterSettings();
        
        if (isMounted) {
          setInitialLoading(false);
        }
      } catch (error) {
        if (isMounted && !abortController.signal.aborted) {
          console.error('[GenerateOrder] Error during data load:', error);
          setInitialLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [user?.franchise?.id, user?.franchise?.franchise_id, user?.client_id]);
  
  // Reload parties when payment modal is opened (fallback in case initial load fails)
  useEffect(() => {
    if (showPaymentModal && parties.length === 0) {
      fetchParties(false);
    }
  }, [showPaymentModal, parties.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target)) {
        setShowSaveDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPrinterSettings = () => {
    // Printer settings are now stored in localStorage via nativeThermalPrinterService
    const settings = nativeThermalPrinter.getSettings();
    setPrinterSettings(settings);
  };
  
  // Fetch franchise GST status for advanced billing
  const fetchFranchiseGstStatus = async () => {
    try {
      const response = await axios.get(`${API}/billing/franchise-gst-status/${user.franchise.id}`);
      if (response.data.success) {
        setFranchiseGstStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching franchise GST status:', error);
    }
  };
  
  // Fetch next token number for today
  const fetchNextToken = async () => {
    try {
      const response = await axios.get(`${API}/billing/next-token/${user.franchise.id}`);
      if (response.data.success) {
        setTokenNo(response.data.next_token.toString());
      }
    } catch (error) {
      console.error('Error fetching next token:', error);
    }
  };
  
  // Effect to fetch GST status when advanced billing is enabled
  useEffect(() => {
    if (isAdvancedBilling && !franchiseGstStatus) {
      fetchFranchiseGstStatus();
    }
    if (isAdvancedBilling && (orderType === 'takeaway' || orderType === 'delivery')) {
      fetchNextToken();
    }
  }, [isAdvancedBilling, orderType]);

  const fetchMenuItems = async (signal) => {
    const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id;
    setMenuLoading(true);
    
    try {
      const response = await axios.get(`${API}/menu-items`, {
        params: { franchise_id: franchiseId },
        headers: getAuthHeaders(),
        signal,
        timeout: 15000
      });
      
      const items = response.data.menu_items || [];
      
      const sortedItems = [...items].sort((a, b) => {
        const categoryCompare = (a.category || '').localeCompare(b.category || '');
        if (categoryCompare !== 0) return categoryCompare;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setMenuItems(sortedItems);
      
      const uniqueCategories = [...new Set(sortedItems.map(item => item.category))].sort();
      setCategories(['All', ...uniqueCategories]);
    } catch (error) {
      if (!signal?.aborted) {
        console.error('Error fetching menu items:', error);
        toast.error('Failed to load menu items');
      }
    } finally {
      setMenuLoading(false);
    }
  };

  // Direct API call for parties
  const fetchParties = async (showToast = false, signal) => {
    const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id;
    
    if (!franchiseId) {
      setParties([]);
      return;
    }
    
    setPartiesLoading(true);
    
    try {
      const response = await axios.get(`${API}/ledger/parties`, {
        params: { franchise_id: franchiseId },
        headers: getAuthHeaders(),
        timeout: 15000,
        signal
      });
      
      const partiesData = response.data?.parties || [];
      setParties(partiesData);
      
      if (showToast) {
        toast.success(`Loaded ${partiesData.length} party ledgers`);
      }
    } catch (error) {
      if (!signal?.aborted) {
        console.error('[fetchParties] ERROR:', error.message);
        setParties([]);
        if (showToast) {
          toast.error('Failed to load party ledgers');
        }
      }
    } finally {
      setPartiesLoading(false);
    }
  };

  const filteredItems = menuItems
    .filter(item => selectedCategory === 'All' || item.category === selectedCategory)
    .filter(item => 
      searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  // Calculate tax details for advanced billing (only for GST-registered franchises)
  const calculateTaxDetails = () => {
    if (!isAdvancedBilling || Object.keys(cart).length === 0 || !franchiseGstStatus?.is_gst_registered) {
      return null;
    }
    
    let subtotal = 0;
    let totalTax = 0;
    const breakdown = [];
    
    Object.values(cart).forEach(item => {
      const menuItem = menuItems.find(m => m.id === item.id);
      const itemPrice = parseFloat(item.price);
      const quantity = item.quantity;
      const lineTotal = itemPrice * quantity;
      
      const taxRateStr = menuItem?.tax_rate || 'exempted';
      const priceInclusive = menuItem?.price_is_inclusive_of_tax || false;
      
      let taxRatePercent = 0;
      if (taxRateStr !== 'exempted') {
        taxRatePercent = parseFloat(taxRateStr.replace('%', '')) || 0;
      }
      
      let taxableAmount, itemTax;
      
      if (priceInclusive && taxRatePercent > 0) {
        taxableAmount = lineTotal / (1 + taxRatePercent / 100);
        itemTax = lineTotal - taxableAmount;
      } else {
        taxableAmount = lineTotal;
        itemTax = lineTotal * (taxRatePercent / 100);
      }
      
      subtotal += taxableAmount;
      totalTax += itemTax;
      
      if (taxRatePercent > 0) {
        breakdown.push({
          item_name: item.name,
          tax_rate: taxRateStr,
          taxable_amount: taxableAmount,
          tax_amount: itemTax,
          hsn_sac: menuItem?.hsn_sac || ''
        });
      }
    });
    
    const grandTotal = subtotal + totalTax;
    
    if (isIgst) {
      return {
        subtotal: subtotal.toFixed(2),
        igst: totalTax.toFixed(2),
        total_tax: totalTax.toFixed(2),
        grand_total: grandTotal.toFixed(2),
        breakdown,
        is_igst: true
      };
    } else {
      const halfTax = totalTax / 2;
      return {
        subtotal: subtotal.toFixed(2),
        cgst: halfTax.toFixed(2),
        sgst: halfTax.toFixed(2),
        total_tax: totalTax.toFixed(2),
        grand_total: grandTotal.toFixed(2),
        breakdown,
        is_igst: false
      };
    }
  };
  
  const taxDetails = isAdvancedBilling ? calculateTaxDetails() : null;

  const audioContextRef = React.useRef(null);
  
  React.useEffect(() => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.log('Audio not supported');
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playTapSound = () => {
    try {
      const audioContext = audioContextRef.current;
      if (!audioContext) return;
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('Tap sound failed:', error);
    }
  };

  const handleAddItem = (item) => {
    playTapSound();
    setCart(prev => ({
      ...prev,
      [item.id]: {
        ...item,
        quantity: (prev[item.id]?.quantity || 0) + 1
      }
    }));
  };

  const handleRemoveItem = (itemId) => {
    playTapSound();
    setCart(prev => {
      const newCart = { ...prev };
      delete newCart[itemId];
      return newCart;
    });
  };

  // Voice order handler — receives matched items from VoiceOrderButton
  const handleVoiceItemsMatched = (matches) => {
    setCart(prev => {
      const newCart = { ...prev };
      matches.forEach(match => {
        const item = menuItems.find(m => m.id === match.menu_item_id);
        if (item) {
          newCart[item.id] = {
            ...item,
            quantity: (newCart[item.id]?.quantity || 0) + match.quantity,
          };
        }
      });
      return newCart;
    });
    playTapSound();
  };

  const calculateTotal = () => {
    return Object.values(cart).reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  };

  // Unified payment selection handler
  const handleSelectPayment = (methodId, partyData = null) => {
    if (partyData) {
      const ledgerValue = `${partyData.party_name}-${partyData.party_id}`;
      setSelectedLedger(ledgerValue);
      setSelectedLedgerName(partyData.party_name);
      setSelectedPartyId(partyData.party_id);
      setPaymentMethod('NONE');
      setPaymentMethodOther('');
      setShowPaymentModal(false);
      // Clear customer when party ledger is selected
      setCustomerData({
        customer_id: null, customer_name_snapshot: null, customer_mobile_snapshot: null,
        customer_tag_status: 'guest', customer_name: null, customer_mobile: null,
      });
      if (customerTagRef.current) customerTagRef.current.reset();
    } else {
      setPaymentMethod(methodId);
      setSelectedLedger('cash');
      setSelectedLedgerName(PAYMENT_METHODS.find(m => m.id === methodId)?.name || 'Cash');
      setSelectedPartyId(null);
      
      if (methodId !== 'other') {
        setPaymentMethodOther('');
        setShowPaymentModal(false);
      }
    }
  };

  const handleConfirmOtherPayment = () => {
    if (paymentMethodOther.trim()) {
      setSelectedLedgerName(paymentMethodOther);
      setShowPaymentModal(false);
    } else {
      toast.error('Please enter a payment method name');
    }
  };

  const getPaymentDisplay = () => {
    if (selectedPartyId) {
      return selectedLedgerName;
    }
    if (paymentMethod === 'other' && paymentMethodOther) {
      return paymentMethodOther;
    }
    return PAYMENT_METHODS.find(m => m.id === paymentMethod)?.name || 'Cash';
  };

  const getPaymentIcon = () => {
    if (selectedPartyId) {
      return BookUser;
    }
    return PAYMENT_METHODS.find(m => m.id === paymentMethod)?.icon || Banknote;
  };

  const handleSaveOrder = async (shouldPrint = false) => {
    if (Object.keys(cart).length === 0) {
      toast.error('Please add at least one item to the order');
      return;
    }

    if (paymentMethod === 'other' && !paymentMethodOther.trim() && !selectedPartyId) {
      toast.error('Please specify the payment method');
      setShowPaymentModal(true);
      return;
    }

    setLoading(true);
    setShowSaveDropdown(false);
    
    try {
      const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id;
      
      const orderItems = Object.values(cart).map(item => {
        const menuItem = menuItems.find(m => m.id === item.id);
        return {
          menu_item_id: item.id,
          item_name_snapshot: item.name,
          item_price_snapshot: parseFloat(item.price),
          quantity: item.quantity
        };
      });

      const orderPayload = {
        franchise_id: franchiseId,
        items: orderItems,
        ledger: selectedPartyId ? selectedLedger : 'cash',
        ledger_party_id: selectedPartyId,
        ledger_name: selectedPartyId ? selectedLedgerName : null,
        payment_method: selectedPartyId ? 'NONE' : paymentMethod,
        payment_method_other: (!selectedPartyId && paymentMethod === 'other') ? paymentMethodOther : null,
        is_advanced_billing: isAdvancedBilling,
        order_type: isAdvancedBilling ? orderType : null,
        table_no: isAdvancedBilling && orderType === 'dine_in' ? tableNo : null,
        token_no: isAdvancedBilling && (orderType === 'takeaway' || orderType === 'delivery') ? tokenNo : null,
        customer_name: customerData.customer_name || null,
        customer_mobile: customerData.customer_mobile || null,
        delivery_address: isAdvancedBilling && orderType === 'delivery' ? deliveryAddress : null,
        customer_gstin: isAdvancedBilling ? customerGstin : null,
        is_igst: isAdvancedBilling ? isIgst : false,
        // FOS Connect
        customer_id: customerData.customer_id || null,
        customer_name_snapshot: customerData.customer_name_snapshot || null,
        customer_mobile_snapshot: customerData.customer_mobile_snapshot || null,
        customer_tag_status: customerData.customer_tag_status || 'guest',
      };

      const response = await axios.post(`${API}/orders`, orderPayload, {
        headers: getAuthHeaders(),
        timeout: 30000
      });
      
      if (response.data.success) {
        // Build success message with customer info
        const custName = customerData.customer_name_snapshot || customerData.customer_name;
        const custMobile = customerData.customer_mobile_snapshot || customerData.customer_mobile;
        const custInfo = custName 
          ? `${custName} (${custMobile})` 
          : custMobile 
            ? custMobile 
            : null;
        toast.success(custInfo 
          ? `Invoice saved — ${custInfo}` 
          : 'Invoice saved successfully!'
        );
        
        // Print receipt if requested
        if (shouldPrint && response.data.order_no) {
          await handlePrintReceipt(response.data.order_no);
        }
        
        // Reset form
        setCart({});
        setSelectedLedger('cash');
        setSelectedLedgerName('Cash');
        setSelectedPartyId(null);
        setPaymentMethod('cash');
        setPaymentMethodOther('');
        
        // Reset advanced billing fields
        if (isAdvancedBilling) {
          setTableNo('');
          setTokenNo('');
          setDeliveryAddress('');
          setCustomerGstin('');
          if (orderType === 'takeaway' || orderType === 'delivery') {
            fetchNextToken();
          }
        }
        
        // Reset FOS Connect customer
        setCustomerData({
          customer_id: null, customer_name_snapshot: null, customer_mobile_snapshot: null,
          customer_tag_status: 'guest', customer_name: null, customer_mobile: null,
        });
        if (customerTagRef.current) customerTagRef.current.reset();
      } else {
        toast.error(response.data.error || 'Failed to save invoice');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      const rawDetail = error.response?.data?.detail;
      const errorMessage = typeof rawDetail === 'string' ? rawDetail
        : Array.isArray(rawDetail) ? rawDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : error.message || 'Failed to save invoice';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = async (orderNo, orderData = null) => {
    try {
      // Check if printer is connected
      const status = nativeThermalPrinter.getStatus();
      
      if (!status.connected) {
        toast.info('No printer connected. Connect a USB thermal printer in Settings.');
        return;
      }
      
      // Build order data for receipt
      const receiptOrderData = orderData || {
        order_no: orderNo,
        date_timestamp: new Date().toISOString(),
        order_type: isAdvancedBilling ? orderType : null,
        table_no: isAdvancedBilling && orderType === 'dine_in' ? tableNo : null,
        token_no: isAdvancedBilling && (orderType === 'takeaway' || orderType === 'delivery') ? tokenNo : null,
        customer_name: customerData.customer_name || null,
        customer_mobile: customerData.customer_mobile || null,
        items: Object.values(cart).map(item => ({
          name: item.name,
          quantity: item.quantity,
          price: parseFloat(item.price),
          line_total: item.quantity * parseFloat(item.price)
        })),
        total_value: calculateTotal(),
        payment_method: selectedPartyId ? 'LEDGER' : paymentMethod,
        ledger_name: selectedPartyId ? selectedLedgerName : null,
        ledger: selectedPartyId ? selectedLedger : 'cash'
      };
      
      // Get franchise data with brand_name from user context
      const franchiseData = {
        brand_name: user?.franchise?.brand_name || user?.franchise?.name || '1946 Filter Coffee & Tea',
        franchise_name: user?.franchise?.franchise_name || user?.franchise?.name || '',
        franchise_address: user?.franchise?.franchise_address || user?.franchise?.address || '',
        contact_number: user?.franchise?.contact_number || user?.franchise?.mobile_number || '',
        pincode: user?.franchise?.pincode || '',
        gstin: user?.franchise?.gstin || ''
      };
      
      // Print using native thermal printer service
      const result = await nativeThermalPrinter.print(receiptOrderData, franchiseData);
      
      if (result.success) {
        toast.success('Receipt printed successfully!');
      } else {
        toast.error(result.error || 'Failed to print receipt');
      }
    } catch (error) {
      console.error('Error printing receipt:', error);
      toast.error('Failed to print receipt: ' + error.message);
    }
  };

  // Handle Save & E-Bill - Skip modal if customer already linked
  const handleOpenEBillModal = () => {
    setShowSaveDropdown(false);
    
    // If FOS Connect customer is linked with a mobile number, skip modal and send directly
    if (customerData.customer_id && customerData.customer_mobile) {
      handleDirectEBill();
      return;
    }
    
    // Otherwise open modal for manual entry
    setEBillData({
      customerName: customerData.customer_name || '',
      phoneNumber: customerData.customer_mobile || ''
    });
    setShowEBillModal(true);
  };

  // Direct E-Bill: save order + send WhatsApp without modal
  const handleDirectEBill = async () => {
    if (Object.keys(cart).length === 0) {
      toast.error('Cart is empty!');
      return;
    }
    
    setSendingEBill(true);
    
    try {
      const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id || user?.franchise_id;
      const orderItems = Object.values(cart).map(item => ({
        menu_item_id: item.id,
        item_name_snapshot: item.name,
        item_price_snapshot: parseFloat(item.price),
        quantity: item.quantity
      }));

      const custName = customerData.customer_name || '';
      const custMobile = customerData.customer_mobile || '';

      const orderData = {
        franchise_id: franchiseId,
        items: orderItems,
        ledger: selectedPartyId ? selectedLedger : 'cash',
        ledger_party_id: selectedPartyId || null,
        ledger_name: selectedPartyId ? selectedLedgerName : null,
        payment_method: selectedPartyId ? 'NONE' : paymentMethod,
        payment_method_other: (!selectedPartyId && paymentMethod === 'other') ? paymentMethodOther : null,
        is_advanced_billing: isAdvancedBilling,
        order_type: isAdvancedBilling ? orderType : null,
        table_no: isAdvancedBilling && orderType === 'dine_in' ? tableNo : null,
        token_no: isAdvancedBilling && (orderType === 'takeaway' || orderType === 'delivery') ? tokenNo : null,
        customer_name: custName,
        customer_mobile: custMobile,
        delivery_address: isAdvancedBilling && orderType === 'delivery' ? deliveryAddress : null,
        customer_gstin: isAdvancedBilling ? customerGstin : null,
        is_igst: isAdvancedBilling ? isIgst : false,
        customer_id: customerData.customer_id || null,
        customer_name_snapshot: custName || null,
        customer_mobile_snapshot: custMobile || null,
        customer_tag_status: customerData.customer_tag_status || 'guest',
      };
      
      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.data.success) {
        const orderNo = response.data.order_no;
        
        try {
          await axios.post(`${API}/orders/${orderNo}/send-whatsapp`, {
            customer_name: custName,
            phone_number: custMobile
          }, {
            headers: { ...getAuthHeaders() }
          });
          
          const displayName = custName ? `${custName} (${custMobile})` : custMobile;
          toast.success(`Invoice saved — E-Bill sent to ${displayName}`);
        } catch (whatsappError) {
          console.error('WhatsApp send error:', whatsappError);
          const whatsappErrorMsg = whatsappError.response?.data?.detail || whatsappError.message || 'Unknown error';
          toast.warning(`Order saved (${orderNo}) but WhatsApp failed: ${whatsappErrorMsg}`);
        }
        
        // Reset cart and customer
        setCart({});
        setCustomerData({
          customer_id: null, customer_name_snapshot: null, customer_mobile_snapshot: null,
          customer_tag_status: 'guest', customer_name: null, customer_mobile: null,
        });
        if (customerTagRef.current) customerTagRef.current.reset();
        navigate('/franchise/dashboard');
      } else {
        toast.error(response.data.error || 'Failed to save order');
      }
    } catch (error) {
      console.error('Direct E-Bill error:', error);
      const rawDetail = error.response?.data?.detail;
      const errorMessage = typeof rawDetail === 'string' ? rawDetail
        : Array.isArray(rawDetail) ? rawDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : error.message || 'Failed to save order';
      toast.error(errorMessage);
    } finally {
      setSendingEBill(false);
    }
  };

  // Handle E-Bill submission - Save order and send via WhatsApp
  const handleSaveAndEBill = async (e) => {
    e.preventDefault();
    
    if (!eBillData.customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    if (!eBillData.phoneNumber.trim()) {
      toast.error('Please enter phone number');
      return;
    }
    
    const phoneDigits = eBillData.phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Please enter a valid phone number (minimum 10 digits)');
      return;
    }
    
    setSendingEBill(true);
    
    try {
      // First save the order (without print)
      const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id || user?.franchise_id;
      
      const orderItems = Object.values(cart).map(item => ({
        menu_item_id: item.id,
        item_name_snapshot: item.name,
        item_price_snapshot: parseFloat(item.price),
        quantity: item.quantity
      }));

      const orderData = {
        franchise_id: franchiseId,
        items: orderItems,
        ledger: selectedPartyId ? selectedLedger : 'cash',
        ledger_party_id: selectedPartyId || null,
        ledger_name: selectedPartyId ? selectedLedgerName : null,
        payment_method: selectedPartyId ? 'NONE' : paymentMethod,
        payment_method_other: (!selectedPartyId && paymentMethod === 'other') ? paymentMethodOther : null,
        is_advanced_billing: isAdvancedBilling,
        order_type: isAdvancedBilling ? orderType : null,
        table_no: isAdvancedBilling && orderType === 'dine_in' ? tableNo : null,
        token_no: isAdvancedBilling && (orderType === 'takeaway' || orderType === 'delivery') ? tokenNo : null,
        customer_name: eBillData.customerName.trim(),
        customer_mobile: eBillData.phoneNumber.trim(),
        delivery_address: isAdvancedBilling && orderType === 'delivery' ? deliveryAddress : null,
        customer_gstin: isAdvancedBilling ? customerGstin : null,
        is_igst: isAdvancedBilling ? isIgst : false,
        // FOS Connect
        customer_id: customerData.customer_id || null,
        customer_name_snapshot: eBillData.customerName.trim() || customerData.customer_name_snapshot || null,
        customer_mobile_snapshot: eBillData.phoneNumber.trim() || customerData.customer_mobile_snapshot || null,
        customer_tag_status: customerData.customer_tag_status || 'guest',
      };
      
      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.data.success) {
        const orderNo = response.data.order_no;
        
        // Now send WhatsApp with the order number (not order ID)
        try {
          await axios.post(`${API}/orders/${orderNo}/send-whatsapp`, {
            customer_name: eBillData.customerName.trim(),
            phone_number: eBillData.phoneNumber.trim()
          }, {
            headers: { ...getAuthHeaders() }
          });
          
          toast.success('Order saved and E-Bill sent via WhatsApp!');
        } catch (whatsappError) {
          console.error('WhatsApp send error:', whatsappError);
          const whatsappErrorMsg = whatsappError.response?.data?.detail || whatsappError.message || 'Unknown error';
          toast.warning(`Order saved (${orderNo}) but WhatsApp failed: ${whatsappErrorMsg}. You can resend from Order Details.`);
        }
        
        // Close modal and reset
        setShowEBillModal(false);
        setEBillData({ customerName: '', phoneNumber: '' });
        
        // Reset cart and navigate
        setCart({});
        navigate('/franchise/dashboard');
      } else {
        toast.error(response.data.error || 'Failed to save order');
      }
    } catch (error) {
      console.error('Save & E-Bill error:', error);
      const rawDetail = error.response?.data?.detail;
      const errorMessage = typeof rawDetail === 'string' ? rawDetail
        : Array.isArray(rawDetail) ? rawDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : error.message || 'Failed to save order';
      toast.error(errorMessage);
    } finally {
      setSendingEBill(false);
    }
  };

  const totalAmount = calculateTotal();
  const cartItemsCount = Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);
  const PaymentIcon = getPaymentIcon();

  const colorClasses = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Generate Order Tour */}
      <GuidedTour
        tourId="generateOrder"
        steps={TOUR_DEFINITIONS.generateOrder?.steps || []}
        forceRun={forceTour}
        onComplete={() => {
          setForceTour(false);
          toast.success('Order generation tour completed!');
        }}
      />
      
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading Menu..." />
        </div>
      )}
      
      {/* Saving Order Loader */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <LogoLoader size="md" text="Saving Order..." />
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-surface border-b border-border shadow-sm">
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 sm:h-10 sm:w-10 p-0 rounded-full"
              onClick={() => navigate('/franchise')}
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-lg md:text-xl font-bold text-primary">Generate Order</h1>
              <p className="text-[10px] sm:text-xs text-secondary truncate max-w-[120px] sm:max-w-none">{user.franchise.name}</p>
            </div>
          </div>
          
          {/* Advanced Billing Toggle */}
          <div data-testid="advanced-billing-toggle" className="flex items-center gap-2 mr-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[10px] sm:text-xs text-secondary whitespace-nowrap">Advanced Billing</span>
              <Switch 
                checked={isAdvancedBilling} 
                onCheckedChange={(checked) => {
                  setIsAdvancedBilling(checked);
                  if (checked) {
                    setShowAdvancedPanel(true);
                  }
                }}
                className="data-[state=checked]:bg-accent"
              />
            </label>
            {isAdvancedBilling && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => setShowAdvancedPanel(!showAdvancedPanel)}
              >
                <FileText className="h-4 w-4 mr-1" />
                <span className="text-xs">{showAdvancedPanel ? 'Hide' : 'Details'}</span>
              </Button>
            )}
          </div>
          
          {/* Mobile Category Toggle */}
          <Button
            variant="outline"
            size="sm"
            className="lg:hidden h-9 sm:h-10 px-2 sm:px-3 rounded-full"
            onClick={() => setShowCategories(!showCategories)}
          >
            <Menu className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
            <span className="hidden sm:inline text-sm">Categories</span>
          </Button>
        </div>

        {/* Advanced Billing Panel */}
        {isAdvancedBilling && showAdvancedPanel && (
          <div className="border-t border-border bg-accent/5 p-3 sm:p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* Order Type Selection */}
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-secondary mb-1.5 block">Order Type</Label>
                  <div className="flex gap-2">
                    {ORDER_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <Button
                          key={type.id}
                          variant={orderType === type.id ? 'default' : 'outline'}
                          size="sm"
                          className={`flex-1 h-10 ${orderType === type.id ? 'bg-accent' : ''}`}
                          onClick={() => setOrderType(type.id)}
                        >
                          <Icon className="h-4 w-4 mr-1.5" />
                          <span className="text-xs">{type.name}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Table/Token Number */}
                <div className="w-full sm:w-32">
                  {orderType === 'dine_in' ? (
                    <>
                      <Label className="text-xs font-semibold text-secondary mb-1.5 block">Table No.</Label>
                      <Input
                        placeholder="T-1"
                        value={tableNo}
                        onChange={(e) => setTableNo(e.target.value)}
                        className="h-10"
                      />
                    </>
                  ) : (
                    <>
                      <Label className="text-xs font-semibold text-secondary mb-1.5 block">Token No.</Label>
                      <Input
                        placeholder="1"
                        value={tokenNo}
                        onChange={(e) => setTokenNo(e.target.value)}
                        className="h-10"
                      />
                    </>
                  )}
                </div>
              </div>
              
              {/* Second Row - Delivery Address & GST */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-3">
                {orderType === 'delivery' && (
                  <div className="flex-1">
                    <Label className="text-xs font-semibold text-secondary mb-1.5 block">Delivery Address</Label>
                    <Input
                      placeholder="Enter delivery address"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      className="h-10"
                    />
                  </div>
                )}
                
                {franchiseGstStatus?.is_gst_registered && (
                  <>
                    <div className="w-full sm:w-48">
                      <Label className="text-xs font-semibold text-secondary mb-1.5 block">Customer GSTIN (B2B)</Label>
                      <Input
                        placeholder="Enter GSTIN"
                        value={customerGstin}
                        onChange={(e) => setCustomerGstin(e.target.value.toUpperCase())}
                        className="h-10"
                        maxLength={15}
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-2 cursor-pointer h-10 px-3 rounded-lg border border-border bg-background">
                        <input
                          type="checkbox"
                          checked={isIgst}
                          onChange={(e) => setIsIgst(e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-xs font-medium">IGST (Inter-state)</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
              
              {/* GST Status Info */}
              {franchiseGstStatus && (
                <div className="mt-3 flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    franchiseGstStatus.is_gst_registered 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {franchiseGstStatus.is_gst_registered ? `GST: ${franchiseGstStatus.gstin}` : 'Non-GST Registered'}
                  </span>
                  <span className="text-xs text-secondary">
                    Invoice Type: {franchiseGstStatus.invoice_type === 'tax_invoice' ? 'Tax Invoice' : 'Bill of Supply'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Category Dropdown */}
        {showCategories && (
          <div className="lg:hidden border-t border-border bg-muted">
            <div className="flex overflow-x-auto gap-2 p-2 sm:p-3 scrollbar-hide">
              {categories.map((category) => (
                <Button
                  key={category}
                  data-testid={`category-${category}-button`}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  className="whitespace-nowrap rounded-full font-bold text-xs sm:text-sm px-3 h-8"
                  onClick={() => {
                    setSelectedCategory(category);
                    setShowCategories(false);
                  }}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Categories Sidebar - Desktop Only */}
        <div data-testid="menu-categories" className="hidden lg:block w-36 xl:w-40 bg-surface border-r border-border overflow-y-auto flex-shrink-0">
          {menuLoading ? (
            <CategoriesSkeleton count={6} />
          ) : (
          <div className="p-3 xl:p-4 space-y-2">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`category-${category}-button`}
                variant={selectedCategory === category ? 'default' : 'ghost'}
                className="w-full h-auto min-h-[2.5rem] py-2 px-2 xl:px-3 rounded-xl font-bold text-xs xl:text-sm uppercase tracking-wider flex items-center justify-center"
                onClick={() => setSelectedCategory(category)}
              >
                <span className="text-center break-words leading-tight">{category}</span>
              </Button>
            ))}
          </div>
          )}
        </div>

        {/* Menu Items Grid */}
        <div className="flex-1 overflow-y-auto pb-28 sm:pb-32">
          <div className="p-2 sm:p-4 md:p-6">
            {/* Search Bar and Image Toggle */}
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search menu items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 sm:pl-10 h-10 sm:h-12 rounded-full border-2 focus:border-accent text-sm sm:text-base"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  )}
                </div>
                {/* Voice Order Button */}
                <div className="relative">
                  <VoiceOrderButton
                    menuItems={menuItems}
                    onItemsMatched={handleVoiceItemsMatched}
                    getAuthHeaders={getAuthHeaders}
                  />
                </div>
                {/* Menu Image Toggle */}
                <button
                  onClick={() => setShowMenuImages(!showMenuImages)}
                  className={`flex items-center gap-1.5 px-3 py-2 h-10 sm:h-12 rounded-full border-2 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap ${
                    showMenuImages 
                      ? 'bg-accent/10 border-accent text-accent' 
                      : 'bg-muted border-border text-muted-foreground'
                  }`}
                  title={showMenuImages ? 'Hide menu images' : 'Show menu images'}
                >
                  <ImageIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{showMenuImages ? 'Images On' : 'Images Off'}</span>
                </button>
              </div>
              {searchQuery && (
                <p className="text-xs sm:text-sm text-secondary mt-2">
                  Found {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
                </p>
              )}
            </div>
            
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-primary mb-3 sm:mb-4">
              {selectedCategory === 'All' ? 'All Items' : selectedCategory}
            </h2>
            {menuLoading ? (
              <MenuItemsSkeleton count={24} />
            ) : filteredItems.length === 0 ? (
              <div className="col-span-full text-center py-8 sm:py-12">
                <Search className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-muted mb-3 sm:mb-4" />
                <p className="text-base sm:text-lg text-secondary mb-2">No items found</p>
                {searchQuery && (
                  <p className="text-xs sm:text-sm text-secondary">
                    Try a different search term or{' '}
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="text-accent hover:underline font-medium"
                    >
                      clear search
                    </button>
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Group items by category */}
                {(() => {
                  const sortedItems = [...filteredItems].sort((a, b) => {
                    const categoryCompare = (a.category || '').localeCompare(b.category || '');
                    if (categoryCompare !== 0) return categoryCompare;
                    return (a.name || '').localeCompare(b.name || '');
                  });
                  
                  const grouped = sortedItems.reduce((acc, item) => {
                    const cat = item.category || 'Uncategorized';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(item);
                    return acc;
                  }, {});
                  
                  const sortedCategories = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                  
                  return sortedCategories.map((category) => (
                    <div key={category}>
                      {/* Category Header/Divider */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-[1px] bg-accent/30 flex-grow"></div>
                        <span className="text-[10px] sm:text-xs font-bold text-accent uppercase tracking-wider px-2 whitespace-nowrap">{category}</span>
                        <div className="h-[1px] bg-accent/30 flex-grow"></div>
                      </div>
                      
                      {/* Items Grid */}
                      <div data-testid="menu-item-grid" className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1 sm:gap-1.5">
                        {grouped[category].map((item) => (
                          <Card
                            key={item.id}
                            data-testid={`menu-item-${item.id}`}
                            className={`menu-item-card shadow-sm border-[0.5px] cursor-pointer transition-all hover:shadow-md relative rounded-sm ${
                              cart[item.id] ? 'border-accent border-2 bg-accent/5' : 'border-gray-300 hover:border-accent hover:border-2'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddItem(item);
                            }}
                          >
                            <CardContent className={`p-1 sm:p-1.5 flex flex-col justify-between h-full rounded-none ${showMenuImages ? 'min-h-[70px] sm:min-h-[80px]' : 'min-h-[45px] sm:min-h-[50px]'}`}>
                              {cart[item.id] && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveItem(item.id);
                                  }}
                                  className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md z-10"
                                  title="Remove from cart"
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              )}

                              <div>
                                <h3 className="font-bold text-[10px] sm:text-xs md:text-sm text-primary leading-tight line-clamp-2">{item.name}</h3>
                              </div>
                              
                              {/* Menu Item Image - only show if toggle is ON */}
                              {showMenuImages && item.image?.data && (
                                <div className="flex-1 flex items-center justify-center my-1">
                                  <img 
                                    src={item.image.data} 
                                    alt={item.name}
                                    className="max-h-8 sm:max-h-10 w-auto object-contain rounded"
                                  />
                                </div>
                              )}
                              
                              <div className="flex justify-between items-center mt-0.5">
                                <p className="text-[9px] sm:text-xs font-bold text-accent">₹{item.price}</p>
                                {cart[item.id] && (
                                  <div className="flex items-center justify-center bg-accent text-white rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4">
                                    <span className="text-[7px] sm:text-[8px] font-bold">{cart[item.id].quantity}</span>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div data-testid="cart-section" className="fixed bottom-0 left-0 right-0 bg-surface border-t border-border shadow-lg z-50">
        {/* FOS Connect Customer Row */}
        <div className="px-2 sm:px-3 md:px-4 pt-2 pb-0">
          <CustomerTagSection
            ref={customerTagRef}
            user={user}
            onCustomerChange={setCustomerData}
            getAuthHeaders={getAuthHeaders}
            disabled={!!selectedPartyId}
          />
        </div>
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 pt-1.5 gap-2 sm:gap-3">
          {/* Total Amount */}
          <div className="flex flex-col min-w-0">
            {isAdvancedBilling && taxDetails ? (
              <>
                <span className="text-[9px] sm:text-xs text-secondary">Subtotal: ₹{taxDetails.subtotal}</span>
                {taxDetails.is_igst ? (
                  <span className="text-[9px] sm:text-xs text-secondary">IGST: ₹{taxDetails.igst}</span>
                ) : (
                  <span className="text-[9px] sm:text-xs text-secondary">
                    CGST: ₹{taxDetails.cgst} | SGST: ₹{taxDetails.sgst}
                  </span>
                )}
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-accent truncate">
                  ₹{taxDetails.grand_total}
                </span>
              </>
            ) : (
              <>
                <span className="text-[9px] sm:text-xs text-secondary">Total ({cartItemsCount})</span>
                <span className="text-lg sm:text-xl md:text-2xl font-bold text-accent truncate">₹{totalAmount.toFixed(2)}</span>
              </>
            )}
          </div>

          {/* Unified Payment Method Button */}
          <Button
            type="button"
            variant="outline"
            data-testid="payment-button"
            onClick={() => setShowPaymentModal(true)}
            className={`h-10 sm:h-11 md:h-12 px-3 sm:px-4 rounded-xl flex items-center gap-2 min-w-[100px] sm:min-w-[140px] ${
              selectedPartyId ? 'border-amber-400 bg-amber-50' : ''
            }`}
          >
            <PaymentIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="font-medium text-xs sm:text-sm truncate">{getPaymentDisplay()}</span>
            {selectedPartyId && <span className="text-[9px] sm:text-xs text-amber-600 font-medium">(Ledger)</span>}
          </Button>

          {/* Printer Settings Button */}
          <Button
            type="button"
            variant="ghost"
            data-testid="printer-settings-button"
            onClick={() => setShowPrinterSettings(true)}
            className="h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12 rounded-xl flex items-center justify-center flex-shrink-0"
            title="Printer Settings"
          >
            <Printer className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          {/* Save Button with Dropdown */}
          <div className="relative flex-shrink-0" ref={saveDropdownRef}>
            <div className="flex">
              <Button
                data-testid="place-order-btn"
                disabled={cartItemsCount === 0 || loading}
                onClick={() => handleSaveOrder(false)}
                className="h-10 sm:h-11 md:h-12 px-4 sm:px-5 md:px-6 bg-primary text-white hover:bg-primary/90 rounded-l-full text-xs sm:text-sm md:text-base font-bold uppercase tracking-wider"
              >
                {loading ? '...' : 'Save'}
              </Button>
              
              <Button
                data-testid="save-dropdown-toggle"
                disabled={cartItemsCount === 0 || loading}
                onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                className="h-10 sm:h-11 md:h-12 px-2 bg-primary text-white hover:bg-primary/90 rounded-r-full border-l border-white/20"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showSaveDropdown ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showSaveDropdown && (
              <div className="absolute bottom-full right-0 mb-2 w-44 sm:w-48 bg-surface rounded-xl shadow-lg border border-border overflow-hidden z-50">
                <button
                  data-testid="save-only-option"
                  onClick={() => handleSaveOrder(false)}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-2 text-primary font-medium text-sm"
                >
                  Save Only
                </button>
                <button
                  data-testid="save-and-print-option"
                  onClick={() => handleSaveOrder(true)}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-2 text-primary font-medium border-t border-border text-sm"
                >
                  <Printer className="h-4 w-4" />
                  Save & Print
                </button>
                <button
                  data-testid="save-and-ebill-option"
                  onClick={handleOpenEBillModal}
                  disabled={loading}
                  className="w-full px-4 py-3 text-left hover:bg-muted flex items-center gap-2 text-green-600 font-medium border-t border-border text-sm"
                >
                  <MessageCircle className="h-4 w-4" />
                  Save & E-Bill
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Printer Settings Modal */}
      <PrinterSettings
        open={showPrinterSettings}
        onOpenChange={setShowPrinterSettings}
        franchiseData={{
          name: user?.franchise?.name || user?.franchise?.franchise_name || '1946 Filter Coffee & Tea',
          address: user?.franchise?.address || '',
          contact: user?.franchise?.contact || user?.franchise?.phone || '',
          gstin: user?.franchise?.gstin || ''
        }}
      />

      {/* E-Bill (WhatsApp) Modal */}
      <Dialog open={showEBillModal} onOpenChange={setShowEBillModal}>
        <DialogContent className="bg-surface max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              Save & Send E-Bill
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveAndEBill} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="ebill-customer-name">Customer Name</Label>
              <Input
                id="ebill-customer-name"
                type="text"
                value={eBillData.customerName}
                onChange={(e) => setEBillData(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Enter customer name"
                className="h-12 mt-2"
                autoFocus
                required
                data-testid="ebill-customer-name"
              />
            </div>

            <div>
              <Label htmlFor="ebill-phone-number">WhatsApp Number</Label>
              <Input
                id="ebill-phone-number"
                type="tel"
                value={eBillData.phoneNumber}
                onChange={(e) => setEBillData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="9876543210 or +919876543210"
                className="h-12 mt-2"
                required
                data-testid="ebill-phone-number"
              />
              <p className="text-xs text-secondary mt-1">Enter 10-digit number or with country code</p>
            </div>

            {/* Order Summary Preview */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
              <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Message Preview
              </p>
              <div className="text-xs text-green-700 space-y-1">
                <p>Hello {eBillData.customerName || '[Customer Name]'}! 👋</p>
                <p>Greetings from <strong>1946 Filter Coffee and Tea</strong></p>
                <p>Thank you for visiting <strong>{user?.franchise?.name || user?.franchise?.franchise_name || 'our outlet'}</strong>!</p>
                <p className="mt-2">📋 <strong>Order Summary</strong></p>
                <p>Items: {Object.values(cart).reduce((sum, item) => sum + item.quantity, 0)}</p>
                <p>Amount: ₹{calculateTotal().toFixed(2)}</p>
                <p className="mt-2 italic">+ Invoice PDF attached</p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEBillModal(false);
                  setEBillData({ customerName: '', phoneNumber: '' });
                }}
                className="flex-1 h-11"
                disabled={sendingEBill}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-11 bg-green-500 text-white hover:bg-green-600"
                disabled={sendingEBill}
                data-testid="ebill-submit-btn"
              >
                {sendingEBill ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    Save & Send
                  </span>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Unified Payment Selection Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        setShowPaymentModal(open);
        if (!open) setPartySearchQuery('');
      }}>
        <DialogContent className="bg-surface max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold">Select Payment</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto mt-4 space-y-4">
            {/* Payment Methods Section - Hidden when party ledger is selected (and no customer tagged) */}
            {(!selectedPartyId || customerData.customer_id) && (
              <div className="space-y-2" data-testid="payment-method-selector">
                <p className="text-xs sm:text-sm font-semibold text-primary px-1">Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((method) => {
                    const Icon = method.icon;
                    const isSelected = paymentMethod === method.id && !selectedPartyId;
                    
                    return (
                      <Card
                        key={method.id}
                        data-testid={`payment-method-${method.id}`}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          isSelected ? 'border-2 border-accent ring-2 ring-accent/20' : 'border border-border'
                        }`}
                        onClick={() => handleSelectPayment(method.id)}
                      >
                        <CardContent className="p-3 flex items-center gap-2">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[method.color]}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-primary text-sm truncate">{method.name}</p>
                            <p className="text-[10px] text-secondary truncate">{method.description}</p>
                          </div>
                          {isSelected && (
                            <div className="h-5 w-5 rounded-full bg-accent flex items-center justify-center ml-auto flex-shrink-0">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Other Payment Method Input */}
                {paymentMethod === 'other' && !selectedPartyId && (
                  <div className="space-y-2 pt-2">
                    <Input
                      type="text"
                      placeholder="Enter payment method name..."
                      value={paymentMethodOther}
                      onChange={(e) => setPaymentMethodOther(e.target.value)}
                      className="h-10 rounded-lg border-2 focus:border-accent"
                      autoFocus
                    />
                    <Button
                      onClick={handleConfirmOtherPayment}
                      disabled={!paymentMethodOther.trim()}
                      className="w-full h-10 bg-primary text-white hover:bg-primary/90 rounded-full font-bold"
                    >
                      Confirm
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Party Ledger Selected Indicator */}
            {selectedPartyId && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <BookUser className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-amber-800">{selectedLedgerName}</p>
                    <p className="text-xs text-amber-600">Debited to Party Ledger</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedPartyId(null);
                      setSelectedLedger('cash');
                      setSelectedLedgerName('Cash');
                      setPaymentMethod('cash');
                    }}
                    className="text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Ledger Parties Section — hidden when customer is tagged */}
            {!customerData.customer_id && (
            <div className="space-y-2 pt-2 border-t border-border" data-testid="ledger-selector">
              <div className="flex items-center justify-between px-1">
                <p className="text-xs sm:text-sm font-semibold text-primary flex items-center gap-2">
                  <BookUser className="h-4 w-4" />
                  Party Ledgers
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="refresh-parties-btn"
                  onClick={() => fetchParties(true)}
                  className="h-7 px-2 text-xs text-secondary hover:text-primary"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
                
              {/* Party Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search party..."
                  value={partySearchQuery}
                  onChange={(e) => setPartySearchQuery(e.target.value)}
                  className="pl-9 h-9 rounded-lg text-sm"
                />
                {partySearchQuery && (
                  <button
                    onClick={() => setPartySearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <div className="max-h-40 overflow-y-auto space-y-2">
                {partiesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-amber-500 mr-2" />
                    <span className="text-sm text-secondary">Loading parties...</span>
                  </div>
                ) : parties.length === 0 ? (
                  <p className="text-xs text-secondary text-center py-3">No party ledgers available</p>
                ) : (
                  parties
                    .filter(party => {
                      if (!partySearchQuery.trim()) return true;
                      const query = partySearchQuery.toLowerCase();
                      return (
                        (party.party_name || '').toLowerCase().includes(query) ||
                        (party.phone_number || '').toLowerCase().includes(query)
                      );
                    })
                    .map((party) => {
                      const isSelected = selectedPartyId === party.party_id;
                      return (
                        <Card
                          key={party.party_id}
                          data-testid={`ledger-party-${party.party_id}`}
                          className={`cursor-pointer transition-all hover:shadow-md ${
                            isSelected ? 'border-2 border-amber-400 ring-2 ring-amber-200 bg-amber-50' : 'border border-border'
                          }`}
                          onClick={() => handleSelectPayment(paymentMethod, party)}
                        >
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <BookUser className="h-4 w-4 text-amber-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-primary text-sm truncate">{party.party_name}</p>
                                <p className="text-[10px] text-secondary">{party.phone_number}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className={`text-xs font-bold ${
                                parseFloat(party.balance) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                ₹{parseFloat(party.balance).toFixed(0)}
                              </p>
                              {isSelected && (
                                <div className="h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center">
                                  <span className="text-white text-xs">✓</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                )}
                {!partiesLoading && parties.length > 0 && parties.filter(party => {
                  if (!partySearchQuery.trim()) return true;
                  const query = partySearchQuery.toLowerCase();
                  return (
                    (party.party_name || '').toLowerCase().includes(query) ||
                    (party.phone_number || '').toLowerCase().includes(query)
                  );
                }).length === 0 && (
                  <p className="text-xs text-secondary text-center py-3">No parties match your search</p>
                )}
              </div>
            </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="md" />
    </div>
  );
};

export default GenerateOrder;
